import { Drawer, Table, type TableProps } from 'antd'
import { useState, type FC } from 'react'
import { FaQuestionCircle } from 'react-icons/fa'

import { Button, Tag } from '@/components/elements'
import {
  SHIPMENT_ORDER_CSV_FIELD_REFERENCE,
  SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS,
  SHIPMENT_ORDER_CSV_STANDARD_HELP
} from '@/constants/common/message.constants'

type HelpColumn = {
  name: string
  required: boolean
}

type HelpRow = HelpColumn & { key: string; description: string }

const HELP = SHIPMENT_ORDER_CSV_STANDARD_HELP

const columns: TableProps<HelpRow>['columns'] = [
  {
    title: HELP.COL_HEADER_NAME,
    key: 'name',
    width: 190,
    render: (_, row) => (
      <span className="text-xs font-medium text-secondary">{row.name}</span>
    )
  },
  {
    title: HELP.COL_HEADER_REQUIRED,
    key: 'required',
    width: 70,
    align: 'center',
    render: (_, row) =>
      row.required ? (
        <Tag size="small" color="red" className="m-0">
          {HELP.REQUIRED_LABEL}
        </Tag>
      ) : (
        <span className="text-2xs text-gray-2">{HELP.OPTIONAL_LABEL}</span>
      )
  },
  {
    title: HELP.COL_HEADER_DESCRIPTION,
    key: 'description',
    render: (_, row) => <span className="text-xs">{row.description}</span>
  }
]

/**
 * 列の説明は、マッピング項目リファレンスの「形式・補足」を使う（同じ項目の説明を 2 か所に書き分けない）。
 * 列名から項目へは、規定フォーマットのヘッダー名と項目の対応表で引く
 */
const descriptionOf = (name: string): string => {
  const field =
    SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS[
      name as keyof typeof SHIPMENT_ORDER_CSV_STANDARD_HEADER_FIELDS
    ]
  return field ? SHIPMENT_ORDER_CSV_FIELD_REFERENCE[field] : ''
}

const toRows = (defs: readonly HelpColumn[]): HelpRow[] =>
  defs.map((def) => ({
    ...def,
    key: def.name,
    description: descriptionOf(def.name)
  }))

const HelpTable: FC<{ title: string; defs: readonly HelpColumn[] }> = ({
  title,
  defs
}) => (
  <div className="flex flex-col gap-1.5">
    <div className="text-xs font-medium text-secondary">{title}</div>
    <Table<HelpRow>
      size="small"
      bordered
      rowKey="key"
      pagination={false}
      columns={columns}
      dataSource={toRows(defs)}
    />
  </div>
)

/**
 * 通常フォーマット（標準取込）の列仕様ヘルプ（#4728）。
 * 各列の変換・挙動と、標準取込でできること（更新インポート・地点自動作成・
 * カスタムキー等）を表示する。内容の信源は docs/csv-column-reference.md §7 ＋
 * 実装確認（詳細は SHIPMENT_ORDER_CSV_STANDARD_HELP のコメント参照）。
 */
export const StandardFormatHelp: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <Button
        size="small"
        icon={<FaQuestionCircle />}
        onClick={() => setIsOpen(true)}
        data-testid="shipment-order-csv-import__help"
      >
        {HELP.OPEN}
      </Button>
      <Drawer
        title={HELP.TITLE}
        width={760}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        data-testid="shipment-order-csv-import__help-drawer"
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium text-secondary">
              {HELP.RULES_TITLE}
            </div>
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
              {HELP.RULES.map((rule) => (
                <li key={rule} className="text-xs text-secondary">
                  {rule}
                </li>
              ))}
            </ul>
          </div>

          {/* 取込は基本 26 列と追加情報 8 列を別のまとまりで読むが、使う側からは 34 列で 1 つのセットとして見せる
              （テンプレートもこの 34 列＋運行計画 8 列＋任意の列で出す） */}
          <HelpTable
            title={HELP.GROUP_BASE}
            defs={[...HELP.BASE_COLUMNS, ...HELP.ADD_INFO_COLUMNS]}
          />
          <HelpTable
            title={HELP.GROUP_DISPATCHED}
            defs={HELP.DISPATCHED_COLUMNS}
          />
          <HelpTable title={HELP.GROUP_OPTIONAL} defs={HELP.OPTIONAL_COLUMNS} />

          <div className="flex flex-col gap-1.5 rounded border border-table-border bg-neutral-3 p-3">
            <div className="text-xs font-medium text-secondary">
              {HELP.CUSTOM_KEY_TITLE}
            </div>
            <div className="text-xs text-secondary">
              {HELP.CUSTOM_KEY_DESCRIPTION}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  )
}
